import { useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/auth-context";
import { useCurrentUser } from "@/hooks/use-current-user";
import { api } from "@/lib/api-client";
import { Alert, AlertDescription } from "@/components/ui/alert";

type CodeResponse = { code: string; expires_at: string };

export default function OAuthAuthorizePage() {
  const { session, isLoading } = useAuth();
  const currentUser = useCurrentUser();
  const [params] = useSearchParams();
  const clientId = params.get("client_id") ?? "";
  const redirectUri = params.get("redirect_uri") ?? "";
  const state = params.get("state") ?? "";
  const scope = params.get("scope") ?? "";
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!clientId || !redirectUri) {
    return (
      <ErrorCard
        title="Invalid request"
        message="Missing client_id or redirect_uri query parameter."
      />
    );
  }

  let validatedRedirect: URL;
  try {
    validatedRedirect = new URL(redirectUri);
  } catch {
    return (
      <ErrorCard
        title="Invalid request"
        message="redirect_uri is not a valid URL."
      />
    );
  }

  if (isLoading) return <LoadingCard />;

  if (!session) {
    const currentUrl = `/oauth/authorize?${params.toString()}`;
    return (
      <Navigate
        to={`/login?redirect=${encodeURIComponent(currentUrl)}`}
        replace
      />
    );
  }

  if (!currentUser || !currentUser.accountId || !currentUser.businessId) {
    return <Navigate to="/no-account" replace />;
  }

  async function handleAllow() {
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.post<CodeResponse>(
        "/oauth/authorization-codes",
        {
          client_id: clientId,
          redirect_uri: redirectUri,
          scope: scope || undefined,
          state: state || undefined,
        },
        { skipImpersonation: true },
      );
      const url = new URL(validatedRedirect.toString());
      url.searchParams.set("code", res.code);
      if (state) url.searchParams.set("state", state);
      window.location.href = url.toString();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to authorize");
      setSubmitting(false);
    }
  }

  function handleDeny() {
    const url = new URL(validatedRedirect.toString());
    url.searchParams.set("error", "access_denied");
    if (state) url.searchParams.set("state", state);
    window.location.href = url.toString();
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 space-y-6">
        <div className="space-y-1">
          <h1 className="text-xl font-bold">Authorize MCP client</h1>
          <p className="text-sm text-muted-foreground">
            <code className="rounded bg-muted px-1 text-xs">{clientId}</code> is
            requesting access to your Adva account.
          </p>
        </div>
        <div className="rounded-md border bg-muted/50 p-3 text-sm">
          <div className="font-medium">Signed in as</div>
          <div className="text-muted-foreground">{currentUser.email}</div>
        </div>
        <div className="space-y-2">
          <p className="text-sm">This will allow the client to:</p>
          <ul className="list-disc pl-5 text-sm text-muted-foreground">
            <li>Read and modify data in your business on your behalf</li>
            <li>Access the same resources you can access</li>
          </ul>
        </div>
        {error && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
        <div className="flex gap-3">
          <button
            onClick={handleDeny}
            className="flex-1 rounded-md border border-input bg-background px-4 py-2 text-sm font-medium hover:bg-accent"
          >
            Cancel
          </button>
          <button
            onClick={handleAllow}
            disabled={submitting}
            className="flex-1 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {submitting ? "Authorizing..." : "Allow access"}
          </button>
        </div>
      </div>
    </div>
  );
}

function LoadingCard() {
  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-background gap-4">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      <p className="text-sm text-muted-foreground">Loading...</p>
    </div>
  );
}

function ErrorCard({ title, message }: { title: string; message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-lg border bg-card p-6 space-y-3">
        <h1 className="text-xl font-bold">{title}</h1>
        <p className="text-sm text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}
